import React, { memo, useEffect, useState, useCallback } from 'react';

import cls from 'classnames';

import Button from '@ott/button';
import { l10n } from '@ott/l10n';
import Modal, { CONTENT_SIZE } from '@ott/modal';

import styles from './BridgerPay.scss';

const modalContentOptions = {
  size: CONTENT_SIZE.WIDE,
  className: styles.modalContainer,
  childrenClassNames: {
    header: styles.modalHeader,
    body: styles.modalBody,
  },
};

const SCRIPT_SRC = 'https://checkout.bridgerpay.com/v2/loader';

type CashierData = {
  cashierKey: string;
  cashierToken: string;
};

export type BridgerPayProps = {
  isDisabled?: boolean;
  buttonClassName?: string;
  getCashierData: () => Promise<CashierData> | CashierData;
  onPaymentClose?: VoidFunction;
  onPaid?: VoidFunction;
  onDeclined?: VoidFunction;
  customerInfo?: {
    phone?: string;
    email?: string;
  };
};

const BridgerPay = ({
  isDisabled,
  buttonClassName,
  getCashierData,
  onPaymentClose,
  onPaid,
  onDeclined,
  customerInfo = {},
}: BridgerPayProps) => {
  const [bridgerContainer, setBridgerContainer] = useState<HTMLDivElement | null>(null);
  const [cashierData, setCashierData] = useState<CashierData | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const bridgerContainerRef = useCallback((node: HTMLDivElement) => {
    if (!node) {
      return;
    }

    setBridgerContainer(node);
  }, []);

  const handleClickPay = async () => {
    if (!cashierData) {
      const { cashierKey, cashierToken } = await getCashierData();

      if (!cashierKey || !cashierToken) {
        return;
      }

      setCashierData({ cashierKey, cashierToken });
    }

    setIsLoading(true);
    setIsOpened(true);
  };

  const handleCloseModal = () => {
    setIsLoading(false);
    setIsOpened(false);
    setCashierData(null);

    if (onPaymentClose) {
      onPaymentClose();
    }
  };

  const handleLoadScript = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    if (!cashierData || !isOpened || !bridgerContainer) {
      return;
    }

    const script = document.createElement('script');
    script.type = 'application/javascript';
    script.src = SCRIPT_SRC;
    script.setAttribute('data-cashier-key', cashierData.cashierKey);
    script.setAttribute('data-cashier-token', cashierData.cashierToken);
    script.setAttribute('data-theme', 'transparent');
    script.setAttribute('data-phone', customerInfo.phone || '');
    script.setAttribute('data-email', customerInfo.email || '');
    script.setAttribute('data-card-holder-name', ' ');
    script.async = true;

    // eslint-disable-next-line no-unused-expressions
    bridgerContainer.appendChild(script);

    script.addEventListener('load', handleLoadScript);

    // eslint-disable-next-line consistent-return
    return () => {
      script.removeEventListener('load', handleLoadScript);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpened, bridgerContainer]);

  const handleDeposit = (data) => {
    const { type } = data.detail;

    if (type === 'pending') {
      return;
    }

    setTimeout(() => {
      setIsLoading(false);
      setIsOpened(false);
      setCashierData(null);

      if (type === 'approved') {
        if (onPaid) {
          onPaid();
        }
      } else if (type === 'declined') {
        if (onDeclined) {
          onDeclined();
        }
      }
    }, 3000);
  };

  useEffect(() => {
    window.addEventListener('[bp]:deposit', handleDeposit);

    return () => {
      window.removeEventListener('[bp]:deposit', handleDeposit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Button
        isDisabled={isDisabled}
        className={cls(styles.payButton, buttonClassName)}
        specificLocators="bridgerpay-button"
        onClick={handleClickPay}
        isLoading={isLoading}
      >
        {l10n('payment.proceed')}
      </Button>
      <Modal
        isOpen={isOpened}
        onClose={handleCloseModal}
        contentOptions={modalContentOptions}
        fullScreenOnMobile={true}
      >
        <div className={cls({ [styles.skeletonWidget]: isLoading })} ref={bridgerContainerRef} />
      </Modal>
    </>
  );
};

export default memo(BridgerPay);
